(ns frontend.view.nev.fund-commitment.view
  (:require
   [frontend.actions.common :as common-actions]
   [reagent.core :as r]
   [common.localization :as loc]
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.field-templates :as templates]
   [frontend.view.nev.events :as events]
   [common.entity :as entity]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.not-found :as not-found]
   [frontend.routing :as routing]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.state.current-session :as session]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.syncing-data :as syncing]
   [frontend.db :as app-db]
   [frontend.view.entity-viewers.components :as nev-components]
   [common.schema.field :as field]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.state.permissions :as f-permissions]
   [frontend.page-components.default-page-layout :as default-page-layout]))

(defn toolbar-buttons
  [{:keys [entity] :as _view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
     [(when-not (:common/disabled? entity)
        (zap-toolbar/delete-button entity [:fund-commitments-grid]))]
     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex gap-2 items-center"}
   [:span
    {:class "text-xl"}
    [:i
     {:class-name (fu/cs :fal (entity/default-icon entity))}]]
   [:div.nev-name-row-container
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :fund/description}}
    :field-layout :f-4}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:fund-commitment :header :field-defs]
            field-defs-timeout-id :fund-commitment-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/fund-commitment
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     (:edit-layout? @header-state)
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn fund-commitment-view
  [{:keys [fund-commitment-id _is-guest?] :as _view-args}]
  (let [initialized? (r/atom false)
        state-atom (r/cursor app-db/app-state [:views :nev :fund-commitment])
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [fund-commitment-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :fund-commitment-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom fund-commitment-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [fund-commitment-id] :as view-args}]
        (let [fund-commitment (get @session/entities fund-commitment-id)
              header-fields [:fund-commitment/overview]
              has-funds-access (session/has-funds-access?)]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? fund-commitment)
                (:not-found @state-atom)
                (and (not has-funds-access)
                     (not (session/fundraising?))))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not fund-commitment)
                    (:common/disabled? fund-commitment))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    view-id :fund-commitment
                    tenant-has-funds? (seq @session/all-funds)
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id
                                             :collapsed?])]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:fund-commitments-grid]
                                               (loc/tr :back-to/fund-commitments))
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              fund-commitment))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :entity          fund-commitment
                        :selected-fields header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (session/event-pane-disabled?)
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id  view-id
                         :ordering (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type fund-commitment))
                         :modules
                         (concat
                          [{:module-id  :fund-commitment-information
                            :header     (loc/tr :nev-module/about-this-entity-type
                                                [(string/lower-case (loc/tr :entity.type/fund-commitment))])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e])}
                                 ;;open settings

                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed? (get-in @session/view-config
                                                (module-collapsed-path
                                                 :fund-commitment-information))
                            :save-module-collapsed! (partial events/save-module-collapsed!
                                                             :entity.type/fund-commitment)
                            :content    [:div
                                         [entity-editor/view
                                          {:state-atom    state-atom
                                           :entity-id     fund-commitment-id
                                           :view-id       view-id
                                           :module-id     :fund-commitment-information
                                           :remove-fields (concat [:fund-commitment/name
                                                                   :fund-commitment/investor
                                                                   :fund-commitment/fund
                                                                   :common/network-drive :common/named-network-drives]
                                                                  (field/entity-tag-fields
                                                                   tenant-schema
                                                                   (:entity/type fund-commitment))
                                                                  header-fields)
                                           :ordering      (get-in
                                                           @session/view-config
                                                           [view-id :modules :module-config
                                                            :fund-commitment-information
                                                            :ordering])}]]}
                           (let [module-id :fund-commitment-fundraising-opportunity
                                 order-config-path [view-id :modules :module-config module-id
                                                    :ordering]]
                             (nev-components/entity-relation-module
                              {:state-atom        state-atom
                               :module-id         module-id
                               :view-id           view-id
                               :ordering          (get-in
                                                   @session/view-config
                                                   order-config-path)
                               :order-config-path order-config-path
                               :module-label      (loc/tr
                                                   :nev-module/fund-commitment-fundraising-opportunity)
                               :entity            fund-commitment
                               :relationship-attribute :fundraising-opportunity/fund-commitment
                               :reverse-relationship? true
                               :hide-when-empty?  true}))
                           (let [module-id :fund-commitment-investor
                                 order-config-path [view-id :modules :module-config module-id
                                                    :ordering]
                                 fund-commitment-investor (:fund-commitment/investor
                                                           fund-commitment)
                                 module-label (if fund-commitment-investor
                                                (loc/tr :nev-module/existing-fund-commitment-investor
                                                        [(loc/tr (->> fund-commitment-investor
                                                                      (get @session/entities)
                                                                      :entity/type))])
                                                (loc/tr :nev-module/fund-commitment-investor))
                                 collapsed-path (module-collapsed-path module-id)
                                 collapsed? (get-in @session/view-config collapsed-path)
                                 header-tools (when (and fund-commitment-investor
                                                         (not collapsed?)
                                                         (not (session/is-read-only-user?)))
                                                [:span.grow.flex.justify-end
                                                 [nev-components/remove-attribute-icon
                                                  {:entity    fund-commitment
                                                   :attribute :fund-commitment/investor}]])]
                             (nev-components/entity-relation-module
                              {:state-atom        state-atom
                               :module-id         module-id
                               :view-id           view-id
                               :ordering          (get-in
                                                   @session/view-config
                                                   order-config-path)
                               :order-config-path order-config-path
                               :module-label      module-label
                               :entity            fund-commitment
                               :header-tools      header-tools
                               :relationship-attribute :fund-commitment/investor}))
                           (when tenant-has-funds?
                             (let [module-id :fund-commitment-fund
                                   order-config-path [view-id :modules :module-config module-id
                                                      :ordering]
                                   fund-commitment-fund (:fund-commitment/fund fund-commitment)
                                   module-label (if fund-commitment-fund
                                                  (loc/tr :nev-module/existing-fund-commitment-fund
                                                          [(loc/tr (->> fund-commitment-fund
                                                                        (get @session/entities)
                                                                        :entity/type))])
                                                  (loc/tr :nev-module/fund-commitment-fund))
                                   collapsed-path (module-collapsed-path module-id)
                                   collapsed? (get-in @session/view-config collapsed-path)
                                   header-tools (when (and fund-commitment-fund
                                                           (not collapsed?)
                                                           (not (session/is-read-only-user?)))
                                                  [:span.grow.flex.justify-end
                                                   [nev-components/remove-attribute-icon
                                                    {:entity            fund-commitment
                                                     :attribute         :fund-commitment/fund
                                                     :tooltip-text      (loc/tr :remove-fund)
                                                     :confirmation-body (loc/tr :remove-fund)}]])]
                               (nev-components/entity-relation-module
                                {:state-atom        state-atom
                                 :module-id         module-id
                                 :view-id           view-id
                                 :ordering          (get-in
                                                     @session/view-config
                                                     order-config-path)
                                 :order-config-path order-config-path
                                 :module-label      module-label
                                 :entity            fund-commitment
                                 :header-tools      header-tools
                                 :relationship-attribute :fund-commitment/fund})))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id fund-commitment-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path module-collapsed-path}))
                          [(when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access? :common/attachments
                                                                        :entity.type/fund-commitment
                                                                        nil))
                             (nev-components/attachments-module view-id
                                                                :fund-commitment-attachments
                                                                fund-commitment))])}]]]
                     (when-not (session/event-pane-disabled?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [fund-commitment-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/fund-commitment
  [{:keys [route-params _view-params]}]
  (let [fund-commitment-id (-> route-params
                               :id
                               js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page fund-commitment"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "fund-commitment-viewer-id-" fund-commitment-id)}
     [fund-commitment-view {:fund-commitment-id fund-commitment-id}]]))
